import React, { useState } from "react";
import {
  Box,
  Chip,
  Collapse,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import {
  FiChevronDown,
  FiChevronUp,
  FiRefreshCw,
  FiClock,
} from "react-icons/fi";

// ── Fallback defaults used when campaign does not supply these arrays ──
const DEFAULT_INTERVAL_HOURS = [3, 6, 9];

const ATTEMPT_COLORS = [
  "#6366f1",
  "#f59e0b",
  "#10b981",
  "#ef4444",
  "#8b5cf6",
  "#0ea5e9",
  "#f97316",
];

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Build a human-readable label like "3h after failure" */
const buildIntervalLabel = (hours) => `${hours}h after failure`;

/** Return the colour for a given attempt index (0-based), cycling if needed */
const attemptColor = (idx) => ATTEMPT_COLORS[idx % ATTEMPT_COLORS.length];

/** Compute when a scheduled (not-yet-fired) attempt will run */
const getScheduledTime = (campaign, intervalHours) => {
  const base = campaign.lastRetry
    ? new Date(campaign.lastRetry)
    : campaign.updatedOn
    ? new Date(campaign.updatedOn)
    : null;
  if (!base) return null;
  return new Date(base.getTime() + intervalHours * 60 * 60 * 1000);
};

const formatRelative = (date) => {
  if (!date) return null;
  const diff = date - Date.now();
  if (diff < 0) return "soon";
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  return h > 0 ? `in ${h}h ${m}m` : `in ${m}m`;
};

// ── Status chip meta ──────────────────────────────────────────────────────────

const statusMeta = {
  in_progress: { label: "Retrying now…", color: "#6366f1", bg: "#ede9fe" },
  completed: { label: "All retries done", color: "#065f46", bg: "#d1fae5" },
  no_failures: { label: "No failures found", color: "#1e40af", bg: "#dbeafe" },
  default: { label: "Smart Retry On", color: "#92400e", bg: "#fef3c7" },
};

// ── Schedule row (future / not-yet-fired attempt) ─────────────────────────────

const ScheduleRow = ({ attempt, intervalHours, campaign, isCurrent }) => {
  const color = attemptColor(attempt - 1);
  const nextTime = getScheduledTime(campaign, intervalHours);

  return (
    <Box
      sx={{
        border: "1.5px dashed",
        borderColor: isCurrent ? color : "divider",
        borderRadius: 2,
        p: 2,
        bgcolor: isCurrent ? color + "08" : "background.default",
        opacity: isCurrent ? 1 : 0.5,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            bgcolor: isCurrent ? color : "#e2e8f0",
            color: isCurrent ? "#fff" : "#94a3b8",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {attempt}
        </Box>

        <Box flex={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="body2"
              fontWeight={600}
              color={isCurrent ? color : "text.secondary"}
            >
              Attempt #{attempt}
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                · {buildIntervalLabel(intervalHours)}
              </Typography>
            </Typography>

            {isCurrent ? (
              <Chip
                label="Pending"
                size="small"
                sx={{
                  bgcolor: color + "18",
                  color,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              />
            ) : (
              <Chip
                label="Waiting"
                size="small"
                sx={{ bgcolor: "#f1f5f9", color: "#94a3b8", fontSize: 11 }}
              />
            )}
          </Stack>

          {isCurrent && nextTime && (
            <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
              <FiClock size={11} color={color} />
              <Typography variant="caption" color={color} fontWeight={600}>
                Scheduled {formatRelative(nextTime)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ·{" "}
                {nextTime.toLocaleString("en-IN", {
                  dateStyle: "short",
                  timeStyle: "short",
                })}
              </Typography>
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

// ── Resolved attempt row ──────────────────────────────────────────────────────

const AttemptRow = ({ entry, intervalHours }) => {
  const { attempt, retriedAt, total, succeeded, failed, resolved } = entry;
  const color = attemptColor(attempt - 1);
  const successRate = total > 0 ? Math.round((succeeded / total) * 100) : 0;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
        bgcolor: "background.default",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            bgcolor: color,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {attempt}
        </Box>

        <Box flex={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" fontWeight={600}>
              Attempt #{attempt}
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                · {buildIntervalLabel(intervalHours)}
              </Typography>
            </Typography>

            {!resolved ? (
              <Chip
                label="Pending"
                size="small"
                sx={{ bgcolor: "#fef3c7", color: "#92400e", fontSize: 11 }}
              />
            ) : (
              <Chip
                label="Resolved"
                size="small"
                sx={{ bgcolor: "#d1fae5", color: "#065f46", fontSize: 11 }}
              />
            )}
          </Stack>

          <Typography variant="caption" color="text.secondary">
            {new Date(retriedAt).toLocaleString("en-IN", {
              dateStyle: "medium",
              timeStyle: "short",
            })}
          </Typography>
        </Box>
      </Stack>

      <Box mb={1}>
        <LinearProgress
          variant="determinate"
          value={resolved ? successRate : 0}
          sx={{
            height: 6,
            borderRadius: 4,
            bgcolor: "#fee2e2",
            "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 4 },
          }}
        />
      </Box>

      <Stack direction="row" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "#94a3b8",
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {total} total
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "#10b981",
            }}
          />
          <Typography variant="caption" color="#10b981" fontWeight={600}>
            {resolved ? succeeded : "—"} sent
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "#ef4444",
            }}
          />
          <Typography variant="caption" color="#ef4444" fontWeight={600}>
            {resolved ? failed : "—"} failed
          </Typography>
        </Stack>

        {resolved && (
          <Typography
            variant="caption"
            color={color}
            fontWeight={700}
            sx={{ ml: "auto !important" }}
          >
            {successRate}% recovered
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const SmartRetryStatus = ({ campaign }) => {
  const [open, setOpen] = useState(true);

  if (!campaign?.smartRetry && !campaign?.retryHistory?.length) return null;

  const {
    smartRetryCount = 0,
    smartRetryStatus,
    retryHistory = [],
    lastRetry,
  } = campaign;

  // ── Dynamic: use campaign arrays when present, fall back to defaults ──
  const intervalHours = campaign.retryIntervals?.length
    ? campaign.retryIntervals
    : DEFAULT_INTERVAL_HOURS;

  const totalAttempts = intervalHours.length;

  // ── Build unified attempt list (history entries + future schedule rows) ──
  const allAttempts = intervalHours.map((hours, idx) => {
    const attempt = idx + 1;
    const historyEntry =
      retryHistory.find((r) => r.attempt === attempt) ?? null;
    return { attempt, hours, historyEntry };
  });

  const meta =
    statusMeta[smartRetryStatus] ??
    (campaign.smartRetry ? statusMeta.default : statusMeta.completed);

  const totalRecovered = retryHistory.reduce(
    (acc, r) => acc + (r.succeeded ?? 0),
    0
  );
  const totalRetried = retryHistory.reduce(
    (acc, r) => acc + (r.succeeded ?? 0) + (r.failed ?? 0),
    0
  );
  const resolvedCount = retryHistory.filter((r) => r.resolved).length;

  // The next pending attempt is the first one with no history entry yet
  const nextPendingAttempt =
    allAttempts.find(({ historyEntry }) => !historyEntry)?.attempt ?? null;

  return (
    <Box
      sx={{
        border: "1.5px solid",
        borderColor: meta.color + "55",
        borderRadius: 2.5,
        overflow: "hidden",
        mb: 2,
      }}
    >
      {/* ── Header ── */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ bgcolor: meta.bg, px: 2, py: 1.5, cursor: "pointer" }}
        onClick={() => setOpen((p) => !p)}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <FiRefreshCw size={15} color={meta.color} />
          <Typography variant="body2" fontWeight={700} color={meta.color}>
            Smart Retry
          </Typography>
          <Chip
            label={meta.label}
            size="small"
            sx={{
              bgcolor: meta.color + "22",
              color: meta.color,
              fontSize: 11,
              fontWeight: 600,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {retryHistory.length}/{totalAttempts} attempts
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2}>
          {totalRetried > 0 && (
            <Typography variant="caption" color="text.secondary">
              {totalRecovered}/{totalRetried} recovered
            </Typography>
          )}
          {lastRetry && (
            <Typography variant="caption" color="text.secondary">
              Last:{" "}
              {new Date(lastRetry).toLocaleString("en-IN", {
                dateStyle: "short",
                timeStyle: "short",
              })}
            </Typography>
          )}
          {open ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />}
        </Stack>
      </Stack>

      {/* ── Body ── */}
      <Collapse in={open}>
        <Stack spacing={1.5} sx={{ p: 2, bgcolor: "background.paper" }}>
          {allAttempts.map(({ attempt, hours, historyEntry }) =>
            historyEntry ? (
              <AttemptRow
                key={attempt}
                entry={historyEntry}
                intervalHours={hours}
              />
            ) : (
              <ScheduleRow
                key={attempt}
                attempt={attempt}
                intervalHours={hours}
                campaign={campaign}
                isCurrent={attempt === nextPendingAttempt}
              />
            )
          )}

          {/* Overall summary — only when 2+ attempts are resolved */}
          {resolvedCount >= 2 && (
            <>
              <Divider />
              <Stack direction="row" justifyContent="space-between" px={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Total across all retries
                </Typography>
                <Typography variant="caption" fontWeight={700}>
                  {totalRecovered} recovered out of {totalRetried}
                  {totalRetried > 0 && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                    >
                      {" "}
                      ({Math.round((totalRecovered / totalRetried) * 100)}%)
                    </Typography>
                  )}
                </Typography>
              </Stack>
            </>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
};

export default SmartRetryStatus;
